import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, ArrowClockwise } from "@phosphor-icons/react";

const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function GstReturns() {
  const [tab, setTab] = useState("gstr1");
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/gstr/${tab}?month=${month}`);
      setData(data);
    } finally { setLoading(false); }
  };
  const loadHistory = async () => setHistory((await api.get("/gstr/history")).data);

  useEffect(() => { load(); }, [tab, month]);
  useEffect(() => { loadHistory(); }, []);

  const filedReturn = async () => {
    try {
      await api.post(`/gstr/file/${tab}?month=${month}`);
      toast.success(`${tab.toUpperCase()} filed for ${month}`);
      loadHistory();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const TABS = [
    { k: "gstr1", label: "GSTR-1 (Outward)" },
    { k: "gstr3b", label: "GSTR-3B (Summary)" },
    { k: "gstr2b", label: "GSTR-2B (ITC)" },
  ];

  return (
    <div className="space-y-5 lb-fade" data-testid="gst-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Compliance</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">GST Returns</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-neutral-500 font-semibold uppercase tracking-wider">Period</label>
          <input type="month" className="lb-input w-40" value={month} onChange={(e) => setMonth(e.target.value)}
                 data-testid="gst-month" />
        </div>
      </div>

      <div className="lb-card">
        <div className="flex border-b border-neutral-200">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)}
                    data-testid={`gst-tab-${t.k}`}
                    className={"px-4 py-3 text-sm font-semibold border-b-2 transition-colors " +
                      (tab === t.k ? "border-[#0A66C2] text-[#0A66C2]" : "border-transparent text-neutral-500 hover:text-[#0A66C2]")}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5" data-testid="gst-content">
          {loading && <div className="text-sm text-neutral-500">Loading…</div>}
          {!loading && tab === "gstr1" && data && (
            <div className="space-y-4">
              <div className="grid grid-cols-5 gap-4">
                {[
                  { l: "Invoices", v: data.totals.count, mono: false },
                  { l: "Taxable", v: fmtINR(data.totals.taxable) },
                  { l: "CGST", v: fmtINR(data.totals.cgst) },
                  { l: "SGST", v: fmtINR(data.totals.sgst) },
                  { l: "IGST", v: fmtINR(data.totals.igst) },
                ].map((k) => (
                  <div key={k.l} className="border border-neutral-200 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{k.l}</div>
                    <div className="font-mono font-bold text-lg mt-1">{k.v}</div>
                  </div>
                ))}
              </div>

              <SectionTable title={`B2B Invoices (${data.b2b.length})`} rows={data.b2b} />
              <SectionTable title={`B2C Invoices (${data.b2c.length})`} rows={data.b2c} />
            </div>
          )}

          {!loading && tab === "gstr3b" && data && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Outward Supplies</div>
                <table className="lb-table">
                  <tbody>
                    <TRow l="Taxable value" v={data.outward.taxable_value} />
                    <TRow l="CGST" v={data.outward.cgst} />
                    <TRow l="SGST" v={data.outward.sgst} />
                    <TRow l="IGST" v={data.outward.igst} />
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">Net Tax Payable</div>
                <div className="lb-kpi">
                  <div className="lb-kpi-label">To pay</div>
                  <div className="lb-kpi-value mono">{fmtINR(data.net_tax_payable)}</div>
                </div>
              </div>
            </div>
          )}

          {!loading && tab === "gstr2b" && data && (
            <div className="p-6 text-sm text-neutral-600 border border-dashed border-neutral-300">
              {data.note}
            </div>
          )}

          <div className="mt-6 flex gap-2">
            <button onClick={load} className="lb-btn lb-btn-ghost lb-btn-sm">
              <ArrowClockwise size={12} /> Refresh
            </button>
            <button onClick={filedReturn} className="lb-btn lb-btn-sm" data-testid="gst-file-btn">
              <PaperPlaneTilt size={12} /> File to GSTN
            </button>
          </div>
        </div>
      </div>

      <div className="lb-card p-5">
        <div className="font-display font-bold mb-3">Filing history</div>
        <table className="lb-table">
          <thead><tr><th>Return</th><th>Period</th><th>Status</th><th>ARN</th><th>Filed on</th></tr></thead>
          <tbody>
            {history.map((f) => (
              <tr key={f.id}>
                <td className="font-semibold">{f.return_type}</td>
                <td className="font-mono text-xs">{f.month}</td>
                <td><span className="lb-badge lb-badge-success">{f.status}</span></td>
                <td className="font-mono text-xs">{f.arn}</td>
                <td className="text-sm">{fmtDate(f.filed_at)}</td>
              </tr>
            ))}
            {!history.length && <tr><td colSpan={5} className="text-center py-6 text-neutral-500">No filings yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TRow({ l, v }) {
  return <tr><td>{l}</td><td className="amt">{fmtINR(v)}</td></tr>;
}

function SectionTable({ title, rows }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">{title}</div>
      <table className="lb-table">
        <thead>
          <tr><th>Invoice #</th><th>Date</th><th>Customer</th><th>GSTIN</th>
              <th className="text-right">Taxable</th><th className="text-right">Tax</th>
              <th className="text-right">Total</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="font-mono text-xs">{r.invoice_no}</td>
              <td className="text-sm">{fmtDate(r.invoice_date)}</td>
              <td className="text-sm">{r.customer_snapshot?.name}</td>
              <td className="font-mono text-xs">{r.customer_snapshot?.gstin || "—"}</td>
              <td className="amt">{fmtINR(r.subtotal)}</td>
              <td className="amt">{fmtINR((r.cgst || 0) + (r.sgst || 0) + (r.igst || 0))}</td>
              <td className="amt font-semibold">{fmtINR(r.total)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={7} className="text-center py-4 text-neutral-500 text-sm">None</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
